"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.findClosestChild = exports.getChildren = exports.getSearchableChildren = exports.getNestedInteractableChildren = exports.getInteractableChildren = void 0;
const trappable_1 = require("../../components/trappable");
const group_1 = require("../../components/group");
const focus_context_manager_1 = require("../managers/focus-context-manager");
const identity_1 = require("./identity");
const sorting_1 = require("./sorting");
const cache_1 = require("../cache");
/**
 * Recursively find all focusable children of a given view. Focusable children are views
 * that can gain focus (GamepadInteractables & GamepadScrollables). This will not find
 * child interactables of other interactables.
 *
 *  @param view - the view whose focusable children we want
 *  @param includeSelf - whether we want to include the given view if it is a Interactable
 *  @param allowAllFixed - web only, whether we allow fixed positioned focus groups in the search when the body is not scrolled properly
 *
 *  @returns all the interactable children of the view
 */
const getInteractableChildren = (view, includeSelf = false, recursive = false) => (0, exports.getChildren)(view, (element) => (0, identity_1.isFocusable)(element) && (includeSelf || view !== element), recursive);
exports.getInteractableChildren = getInteractableChildren;
/**
 * Given a list of interactables (GamepadScrollable or GamepadInteractable) returns a list of the recursively discovered
 * interactables. We must do this since GamepadScrollable is an Interactable that can also contain interactables within itself.
 * If a GamepadScrollable does not contain interactables within itself, we will just append it to the returned list
 *
 *  @param interactables - the list of interactables
 *
 *  @returns the recursively discovered interactables
 */
const getNestedInteractableChildren = (view) => (0, exports.getInteractableChildren)(view, false, true);
exports.getNestedInteractableChildren = getNestedInteractableChildren;
/**
 * Recursively find all searchable children of a given view. Searchable children are views
 * that are used in our focus search (GamepadInteractables, GamepadScrollables, & FocusGroups).
 * This will not find child searchables of other searchables.
 *
 *  @param view - the view whose focusable children we want

 *  @returns all the child components of the view
 */
const getSearchableChildren = (view, includeSelf = false) => (0, exports.getChildren)(view, (element) => ((0, identity_1.isFocusable)(element) || (0, group_1.isGroup)(element)) && (includeSelf || view !== element));
exports.getSearchableChildren = getSearchableChildren;
/**
 * Recursively find all children of a given view that match the given predicate.
 *
 *  @param view - the view whose children we want

 *  @returns all the child components of the view
 */
const getChildren = (view, predicate, recurseOnPredicate = false) => {
    if (!view) {
        return [];
    }
    const focusContext = (0, focus_context_manager_1.getFocusContext)();
    if (focusContext === focus_context_manager_1.FocusContext.None) {
        return [];
    }
    if (view instanceof HTMLElement && (0, identity_1.isNonNavigable)(view)) {
        return [];
    }
    const activeContainers = (0, focus_context_manager_1.getActiveContainers)();
    const searchableChildren = [];
    if (focusContext === focus_context_manager_1.FocusContext.Global) {
        if ((0, trappable_1.isFocusContainer)(view)) {
            return [];
        }
        else {
            return getChildrenHelper(view, predicate, recurseOnPredicate);
        }
    }
    for (const focusContainer of activeContainers) {
        const focusContainerElement = focusContainer.getElement();
        if (!focusContainerElement) {
            continue;
        }
        // If parent view is a child of a currently focused focus container
        // just return the contents of view
        if (focusContainerElement.contains(view) && view !== focusContainerElement) {
            searchableChildren.push(...getChildrenHelper(view, predicate, recurseOnPredicate));
            return searchableChildren;
        }
        // If parent view is a parent to focus containers, append the contents
        // of each focus container that it is a parent of
        if (view.contains(focusContainerElement)) {
            // Make sure anything between focusContainerElement and view doesn't have non-navigable
            let currentNode = focusContainerElement;
            while (currentNode && currentNode !== view) {
                if (currentNode instanceof HTMLElement && (0, identity_1.isNonNavigable)(currentNode)) {
                    continue;
                }
                currentNode = currentNode.parentElement;
            }
            searchableChildren.push(...getChildrenHelper(focusContainerElement, predicate, recurseOnPredicate));
        }
        if (view === focusContainerElement) {
            break;
        }
    }
    return searchableChildren;
};
exports.getChildren = getChildren;
/**
 * Recursively searches through the child nodes of a given HTML element (`view`) and accumulates those
 * that satisfy a given predicate. The search can be controlled to either stop or continue within child
 * nodes that meet the predicate, based on the `recurseOnPredicate` flag.
 *
 * @param view The HTML element whose child nodes are to be searched.
 * @param predicate A function that takes an HTMLElement and returns a boolean indicating whether the
 * element satisfies a certain condition.
 * @param recurseOnPredicate A boolean flag that determines the behavior of the search:
 *  - If `true`, the search will continue recursively within child nodes that meet the predicate.
 *  - If `false`, the search will stop for any branch once a child node meets the predicate.
 * @returns An array of HTMLElements that are children of `view` and meet the condition specified by the
 * predicate. The search can be deep, including children of children, depending on the
 * `recurseOnPredicate` flag.
 */
const getChildrenHelper = (view, predicate, recurseOnPredicate) => {
    const searchableChildren = [];
    if (view instanceof HTMLElement && (0, identity_1.isNonNavigable)(view)) {
        return searchableChildren;
    }
    const meetsPredicate = predicate(view);
    if (meetsPredicate) {
        searchableChildren.push(view);
        if (!recurseOnPredicate) {
            return searchableChildren;
        }
    }
    for (let i = 0; i < view.childNodes.length; i++) {
        const subView = view.childNodes[i];
        if (!(subView instanceof HTMLElement)) {
            continue;
        }
        if ((0, trappable_1.isFocusContainer)(subView)) {
            continue;
        }
        searchableChildren.push(...getChildrenHelper(subView, predicate, recurseOnPredicate));
    }
    return searchableChildren;
};
const findClosestChild = (containers, rect) => {
    (0, cache_1.clearCache)();
    let closestElement;
    let shortestDistance = Infinity;
    for (const container of containers) {
        const children = (0, exports.getNestedInteractableChildren)(container);
        for (const child of children) {
            const distance = (0, sorting_1.calculateDistance)(child, rect);
            if (distance < shortestDistance) {
                shortestDistance = distance;
                closestElement = child;
            }
        }
    }
    return closestElement;
};
exports.findClosestChild = findClosestChild;
//# sourceMappingURL=data:application/json;base64,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